import { ChevronRight, Clock } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSettings } from "../../contexts/SettingsContext";
import { useBackend } from "../../lib/backend";
import { FintheonNumericText } from "../numbers/FintheonNumericText";
import { TimelineFeed } from "./TimelineFeed";
import { TimelineFilters } from "./TimelineFilters";
import {
  getTimelineCutoff,
  groupTimelineByDate,
  TIMELINE_THREADS,
  type TimelineFeedItem,
} from "./timeline-model";
import { itemMatchesTimelineThread } from "./timeline-thread-rules";

interface TimelineOverlayProps {
  open: boolean;
  onClose: () => void;
}

export function TimelineOverlay({ open, onClose }: TimelineOverlayProps) {
  const backend = useBackend();
  const { preferences } = useSettings();
  const [items, setItems] = useState<TimelineFeedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedThread, setSelectedThread] = useState("all");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [timeRange, setTimeRange] = useState("all");
  const [macroFilter, setMacroFilter] = useState<Set<number>>(new Set([3, 4]));

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    backend.riskflow
      .list({ limit: 200, minMacroLevel: 1 })
      .then((response: { items?: TimelineFeedItem[] }) => {
        setItems(Array.isArray(response.items) ? response.items : []);
      })
      .catch(() => {
        setItems([]);
        setError("Timeline unavailable. Try again when RiskFlow reconnects.");
      })
      .finally(() => setLoading(false));
  }, [backend, open]);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose, open]);

  const filteredItems = useMemo(() => {
    const cutoff = getTimelineCutoff(timeRange);
    return items.filter((item) => {
      if (!itemMatchesTimelineThread(item, selectedThread)) return false;
      if (macroFilter.size > 0 && !macroFilter.has(item.macroLevel ?? 2))
        return false;
      return !cutoff || new Date(item.publishedAt) >= cutoff;
    });
  }, [items, macroFilter, selectedThread, timeRange]);

  const threadCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      for (const thread of TIMELINE_THREADS) {
        if (
          thread.slug !== "all" &&
          itemMatchesTimelineThread(item, thread.slug)
        ) {
          counts.set(thread.slug, (counts.get(thread.slug) ?? 0) + 1);
        }
      }
    }
    return counts;
  }, [items]);

  const selectThread = useCallback((slug: string) => {
    setSelectedThread(slug);
    setDropdownOpen(false);
  }, []);
  const activeThread =
    TIMELINE_THREADS.find((thread) => thread.slug === selectedThread) ??
    TIMELINE_THREADS[0];

  return (
    <aside
      aria-label="Timeline"
      aria-hidden={!open}
      data-timeline-contained="main-surface"
      className={`fintheon-side-surface fintheon-drawer-left absolute inset-y-0 left-0 z-50 flex w-[min(380px,100%)] flex-col bg-[var(--fintheon-bg)] transition-[transform,opacity] duration-300 ease-out ${
        open
          ? "translate-x-0 opacity-100"
          : "pointer-events-none -translate-x-full opacity-0"
      }`}
    >
      <div className="flex shrink-0 items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-[var(--fintheon-accent)]" />
          <h2 className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[var(--fintheon-accent)]">
            Timeline
          </h2>
          <span className="font-mono text-[10px] text-[var(--fintheon-muted)]/42">
            <FintheonNumericText text={filteredItems.length} kind="count" />
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-accent)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-accent)]/55"
          aria-label="Close Timeline"
          title="Close"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <TimelineFilters
        dropdownOpen={dropdownOpen}
        macroFilter={macroFilter}
        selectedThread={selectedThread}
        threadCounts={threadCounts}
        timeRange={timeRange}
        onDropdownToggle={() => setDropdownOpen((value) => !value)}
        onMacroToggle={(level) =>
          setMacroFilter((current) => {
            const next = new Set(current);
            if (next.has(level)) next.delete(level);
            else next.add(level);
            return next;
          })
        }
        onThreadSelect={selectThread}
        onTimeRangeChange={setTimeRange}
      />
      <TimelineFeed
        activeColor={activeThread.color}
        dateGroups={groupTimelineByDate(filteredItems)}
        error={error}
        loading={loading}
        showDirectionalScoring={
          preferences.riskflowFilters?.showDirectionalScoring ?? true
        }
      />
    </aside>
  );
}

export function TimelineToggleButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="fintheon-timeline-toggle-tab"
      aria-label="Open Timeline"
      title="Open Timeline"
    >
      <Clock className="h-3.5 w-3.5" />
    </button>
  );
}
